import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';

import Button from 'modules/_shared/components/Buttons';
import { setModal } from 'modules/_shared/redux/modalActions';
import { Council, Document } from 'types';
import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { addDocument } from 'modules/documents/redux/documentActions';
import fileTypes from 'constants/fileTypes';
import documentTypes from 'constants/documentTypes';
import { ScreenSizes } from 'constants/sizes';
import UploadDocument from 'modules/documents/components/UploadDocument';
import useGetDocumentData from 'modules/administratives/pages/CouncilDetails/hooks/useGetDocumentData';
import councilTemplateTypes from 'modules/administratives/pages/CouncilDetails/constants/councilTemplateTypes';
import signatureTypes from 'constants/signatureTypes';
import EmbedPDFViewer from 'modules/administratives/components/EmbedPDFViewer';
import RequestSignatureModal from '../RequestSignatureModal';

type EditCouncilCertificateEditorProps = {
  size?: ScreenSizes;
  council: Council;
};

const EditCouncilCertificateEditor: FC<EditCouncilCertificateEditorProps> = ({
  council,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const society = useSelector((state: State) => state.society.actualSociety);

  const [isLoading, setIsLoading] = useState(false);
  const [certificateFile, setCertificateFile] = useState<File | undefined>(
    undefined
  );

  // Get template data for certificate
  const certificateTemplateData = useGetDocumentData({
    documentId: council.actCertificateDocument ?? null,
    templateId: council.actCertificateTemplate ?? null,
  });

  const {
    documentFrame: certificateDocumentFrame,
    templateName: certificateTemplateName,
  } = certificateTemplateData.data;
  const { isLoading: isLoadingCertificateTemplate } = certificateTemplateData;

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const renderCertificatePreview = () => {
    const certificateFileUrl = certificateFile
      ? URL.createObjectURL(certificateFile)
      : null;
    const certificateTemplateUrl = certificateDocumentFrame ?? null;

    const certificatePreviewUrl = certificateTemplateUrl ?? certificateFileUrl;

    return <EmbedPDFViewer fileUrl={certificatePreviewUrl ?? null} />;
  };

  const uploadDocument = async (
    file: File,
    documentName: string,
    description: string
  ): Promise<Document | null> => {
    try {
      const newDocument = {
        file,
        date: new Date(),
        size: file.size,
        name: documentName,
        author: user?._id,
        society: society?._id,
        fileType: fileTypes[file.type as keyof typeof fileTypes],
        category: documentTypes.DOCUMENTS,
        subcategory: 'COUNCIL',
        description,
      };

      const uploadedDocument = await store.dispatch(addDocument(newDocument));
      return uploadedDocument;
    } catch (error) {
      return null;
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const newCouncilData: Partial<Council> = {
        _id: council._id,
      };

      // Upload certificate document if file is selected
      if (certificateFile) {
        const uploadedCertificateDocument = await uploadDocument(
          certificateFile,
          `Certificate-${council.name}-${
            new Date().toISOString().split('T')[0]
          }.pdf`,
          t('Council Certificate Document')
        );
        if (uploadedCertificateDocument) {
          newCouncilData.actCertificateDocument =
            uploadedCertificateDocument._id;
        }
      }

      // Only update if there are changes
      if (certificateFile) {
        await store.dispatch(updateCouncil(newCouncilData));
        await store.dispatch(addAlert(alertBodyTypes.COUNCIL_UPDATED));
      }

      dispatch(setModal(null));
    } catch (error) {
      await store.dispatch(addAlert(alertBodyTypes.ERROR_UPDATING_COUNCIL));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestCertificateSignature = () => {
    if (!council._id || !certificateTemplateData.data.document) {
      return;
    }

    dispatch(
      setModal(
        <RequestSignatureModal
          councilId={council._id}
          document={certificateTemplateData.data.document}
          templateType={councilTemplateTypes.CERTIFICATION}
          signatureType={signatureTypes.COUNCIL_CERTIFICATE}
        />
      )
    );
  };

  return (
    <>
      <Modal.Header>
        <h4 className="modal-title mb-0">
          <span>{t('ReplaceCertificate')}</span>
        </h4>

        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="nk-modal">
          <p className="text-muted mb-3">
            {t('ReplaceCouncilCertificateDescription')}
          </p>

          <div className="mb-3">
            <UploadDocument
              label="SelectFile"
              labelClassName="h6"
              file={certificateFile}
              setFile={setCertificateFile}
              idIndex={2}
            />
          </div>

          {/* Show template preview or uploaded file preview */}
          <div className="mt-4">
            <label className="form-label h6">{t('Preview')}</label>
            {renderCertificatePreview()}
          </div>

          <div className="mt-3 d-flex justify-content-end">
            <Button onClick={handleRequestCertificateSignature}>
              <em className="icon ni ni-edit-alt mr-1" />
              {t('RequestSignature')}
            </Button>
          </div>

          {(isLoading || isLoadingCertificateTemplate) && <CustomLoading />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!certificateFile || isLoading}
        >
          {isLoading ? t('Saving...') : t('Save')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EditCouncilCertificateEditor;
